import { http } from "./client";
import type {
  AliasDetail,
  AliasSummary,
  AuditEntry,
  BlacklistTTLPolicy,
  SecuritySettings,
  BlackholeList,
  BlackholeRelease,
  BlockDuration,
  Feed,
  FeedView,
  Filter,
  FilterInput,
  License,
  LicenseCheckResult,
  LicenseInput,
  LicenseInstance,
  LicenseWithKey,
  Me,
  NodeEnrollment,
  NodeSummary,
  PamRule,
  PamRuleInput,
  PermissionDef,
  RoleView,
  RuleList,
  RuleLookup,
  RulePage,
  RuleQuery,
  ScopeView,
  UserGrant,
  UserSummary,
} from "./types";
import type { TokenPair } from "./tokens";

/**
 * Discord snowflakes (~1.1e18) exceed Number.MAX_SAFE_INTEGER, so JSON.parse
 * would silently round them. Quote every `discord_id` number before parsing.
 */
export function quoteDiscordIds(text: string): string {
  return text.replace(/("discord_id"\s*:\s*)(-?\d+)/g, '$1"$2"');
}

const enc = encodeURIComponent;

// ---- auth -------------------------------------------------------------

export const authApi = {
  discordCallback: (code: string, state: string) =>
    http.get<TokenPair>("/api/v1/auth/discord/callback", { query: { code, state }, anonymous: true }),
  devToken: () => http.post<TokenPair>("/api/v1/auth/dev/token", undefined, { anonymous: true }),
  logout: (refreshToken: string) =>
    http.post<void>("/api/v1/auth/logout", { refresh_token: refreshToken }, { anonymous: true }),
};

// ---- users / rbac -----------------------------------------------------

interface WireScope {
  ID: string;
  Name: string;
  Permissions: string[] | null;
}

interface WireRole {
  ID: string;
  Name: string;
  Scopes: WireScope[] | null;
}

const toScope = (s: WireScope): ScopeView => ({ id: s.ID, name: s.Name, permissions: s.Permissions ?? [] });

export const usersApi = {
  me: async (): Promise<Me> => {
    const me = await http.get<Me>("/api/v1/users/me", { transformText: quoteDiscordIds });
    return { ...me, effective_permissions: me.effective_permissions ?? [] };
  },
  list: async () => (await http.get<UserSummary[] | null>("/api/v1/users", { transformText: quoteDiscordIds })) ?? [],
  grant: async (id: string): Promise<UserGrant> => http.get<UserGrant>(`/api/v1/users/${enc(id)}/grant`),
  assignRole: (userId: string, roleId: string) => http.post<void>(`/api/v1/users/${enc(userId)}/roles`, { role_id: roleId }),
  assignScope: (userId: string, scopeId: string) =>
    http.post<void>(`/api/v1/users/${enc(userId)}/scopes`, { scope_id: scopeId }),
  unassignRole: (userId: string, roleId: string) => http.del<void>(`/api/v1/users/${enc(userId)}/roles/${enc(roleId)}`),
  unassignScope: (userId: string, scopeId: string) =>
    http.del<void>(`/api/v1/users/${enc(userId)}/scopes/${enc(scopeId)}`),
  roles: async (): Promise<RoleView[]> =>
    ((await http.get<WireRole[] | null>("/api/v1/roles")) ?? []).map((r) => ({
      id: r.ID,
      name: r.Name,
      scopes: (r.Scopes ?? []).map(toScope),
    })),
  createRole: (name: string, description: string, scopeIds: string[]) =>
    http.post<{ id: string }>("/api/v1/roles", { name, description, scope_ids: scopeIds }),
  scopes: async (): Promise<ScopeView[]> => ((await http.get<WireScope[] | null>("/api/v1/scopes")) ?? []).map(toScope),
  createScope: (name: string, description: string, permissionIds: string[]) =>
    http.post<{ id: string }>("/api/v1/scopes", { name, description, permission_ids: permissionIds }),
  permissions: async () => (await http.get<PermissionDef[] | null>("/api/v1/permissions")) ?? [],
};

// ---- audit ------------------------------------------------------------

interface WireAuditEntry {
  ID: string;
  ActorUserID: string;
  Action: string;
  TargetType: string;
  TargetID: string;
  Detail: Record<string, unknown> | null;
  CreatedAt: string;
}

export const auditApi = {
  list: async (limit: number, offset: number): Promise<AuditEntry[]> =>
    ((await http.get<WireAuditEntry[] | null>("/api/v1/audit", { query: { limit, offset } })) ?? []).map((e) => ({
      id: e.ID,
      actorUserId: e.ActorUserID,
      action: e.Action,
      targetType: e.TargetType,
      targetId: e.TargetID,
      detail: e.Detail,
      createdAt: e.CreatedAt,
    })),
};

// ---- nodes ------------------------------------------------------------

export const nodesApi = {
  list: async () => (await http.get<NodeSummary[] | null>("/api/v1/nodes")) ?? [],
  enroll: (name: string) => http.post<NodeEnrollment>("/api/v1/nodes", { name }),
  revoke: (id: string) => http.post<void>(`/api/v1/nodes/${enc(id)}/revoke`),
};

// ---- blacklist / whitelist ---------------------------------------------

/** A single IP or a CIDR, as the add/remove body expects. */
export function ruleBody(value: string): { ip: string } | { cidr: string } {
  return value.includes("/") ? { cidr: value } : { ip: value };
}

/** The add body for a blacklist entry's duration; whitelist adds never carry one. */
export function durationBody(d: BlockDuration | undefined): { ttl_sec?: number; permanent?: true } {
  if (!d || d.mode === "default") return {};
  return d.mode === "permanent" ? { permanent: true } : { ttl_sec: d.sec };
}

export const rulesApi = {
  page: (list: RuleList, q: RuleQuery, signal?: AbortSignal) =>
    http.get<RulePage>(`/api/v1/rules/${list}`, { query: { ...q }, signal }),
  lookup: (list: RuleList, ip: string) => http.get<RuleLookup>(`/api/v1/rules/${list}/lookup`, { query: { ip } }),
  /** Adding an entry that already exists replaces this operator claim's expiry (ADR-0044). */
  add: (list: RuleList, value: string, duration?: BlockDuration) =>
    http.post<void>(`/api/v1/rules/${list}`, { ...ruleBody(value), ...(list === "blacklist" ? durationBody(duration) : {}) }),
  /** Removes the entry whatever claims it — feeds and auto-blocks included. */
  remove: (list: RuleList, value: string) => http.del<void>(`/api/v1/rules/${list}`, ruleBody(value)),
  ttlPolicy: () => http.get<BlacklistTTLPolicy>("/api/v1/rules/blacklist/ttl"),
  setTTLPolicy: (p: BlacklistTTLPolicy) => http.put<BlacklistTTLPolicy>("/api/v1/rules/blacklist/ttl", p),
};

// ---- auto-blackholes (ADR-0050) ----------------------------------------

export const blackholesApi = {
  list: async (): Promise<BlackholeList> => {
    const r = await http.get<BlackholeList>("/api/v1/rules/blackholes", { query: { limit: 5000 } });
    return { items: r.items ?? [], total: r.total ?? 0 };
  },
  /** Drops only the escalation claims; feed/manual blocks stay. */
  release: (ips: string[]) => http.post<BlackholeRelease>("/api/v1/rules/blackholes/release", { ips }),
};

// ---- PAM ----------------------------------------------------------------

export const pamApi = {
  list: async () => (await http.get<PamRule[] | null>("/api/v1/pam/rules")) ?? [],
  create: (rule: PamRuleInput) => http.post<PamRule>("/api/v1/pam/rules", rule),
  update: (id: string, rule: PamRuleInput) => http.put<PamRule>(`/api/v1/pam/rules/${enc(id)}`, rule),
  remove: (id: string) => http.del<void>(`/api/v1/pam/rules/${enc(id)}`),
};

// ---- aliases / feeds ------------------------------------------------------

export const aliasesApi = {
  list: async () => (await http.get<AliasSummary[] | null>("/api/v1/aliases")) ?? [],
  get: async (name: string): Promise<AliasDetail> => {
    const a = await http.get<AliasDetail>(`/api/v1/aliases/${enc(name)}`);
    return { ...a, entries: a.entries ?? [] };
  },
  createStatic: (name: string, entries: string[]) => http.post<AliasDetail>("/api/v1/aliases", { name, entries }),
  createFeed: (name: string, feed: Feed | Omit<Feed, "name" | "enabled">) =>
    http.post<AliasSummary>("/api/v1/aliases", {
      name,
      feed: {
        url: feed.url,
        regex: feed.regex,
        interval_sec: feed.interval_sec,
        auth_header: feed.auth_header ?? "",
        auth_header_value: feed.auth_header_value ?? "",
        api_key_env: feed.api_key_env ?? "",
      },
    }),
  remove: (name: string) => http.del<void>(`/api/v1/aliases/${enc(name)}`),
  addEntry: (name: string, value: string) => http.post<void>(`/api/v1/aliases/${enc(name)}/entries`, { value }),
  removeEntry: (name: string, value: string) => http.del<void>(`/api/v1/aliases/${enc(name)}/entries`, { value }),
};

export const feedsApi = {
  list: async () => (await http.get<FeedView[] | null>("/api/v1/feeds")) ?? [],
  /** Create-or-update by name. `enabled` must be explicit: omitted means false. */
  upsert: (feed: Feed) => http.post<void>("/api/v1/feeds", feed),
  remove: (name: string) => http.del<void>(`/api/v1/feeds/${enc(name)}`),
};

// ---- filters ----------------------------------------------------------------

interface WireFilter {
  ID: string;
  Name: string;
  KernelIndex: number;
  PPS: number;
  Burst: number;
  PerIPPPS: number;
  PerIPBurst: number;
  BandwidthMbps: number;
  BlackholeAfter: number;
  BlackholeWindowSec: number;
  AutoBlackhole: boolean;
  Enabled: boolean;
}

export function fromWireFilter(f: WireFilter): Filter {
  return {
    id: f.ID,
    name: f.Name,
    kernelIndex: f.KernelIndex,
    pps: f.PPS,
    burst: f.Burst,
    perIpPps: f.PerIPPPS,
    perIpBurst: f.PerIPBurst,
    bandwidthMbps: f.BandwidthMbps,
    blackholeAfter: f.BlackholeAfter,
    blackholeWindowSec: f.BlackholeWindowSec,
    autoBlackhole: f.AutoBlackhole,
    enabled: f.Enabled,
  };
}

export function toFilterInput(f: Filter): FilterInput {
  return {
    name: f.name,
    pps: f.pps,
    burst: f.burst,
    per_ip_pps: f.perIpPps,
    per_ip_burst: f.perIpBurst,
    bandwidth_mbps: f.bandwidthMbps,
    blackhole_after: f.blackholeAfter,
    blackhole_window_sec: f.blackholeWindowSec,
    auto_blackhole: f.autoBlackhole,
    enabled: f.enabled,
  };
}

export const filtersApi = {
  list: async () => ((await http.get<WireFilter[] | null>("/api/v1/filters")) ?? []).map(fromWireFilter),
  create: async (input: FilterInput) => fromWireFilter(await http.post<WireFilter>("/api/v1/filters", input)),
  /** PUT overwrites every numeric field, so always send the full filter. */
  update: async (name: string, input: FilterInput) =>
    fromWireFilter(await http.put<WireFilter>(`/api/v1/filters/${enc(name)}`, input)),
  remove: (name: string) => http.del<void>(`/api/v1/filters/${enc(name)}`),
};

// ---- licenses (DECISIONS.md ADR-0045) ----------------------------------------

export const licensesApi = {
  list: async (q = "") =>
    (await http.get<License[] | null>(`/api/v1/licenses${q ? `?q=${enc(q)}` : ""}`)) ?? [],
  create: (input: LicenseInput) => http.post<LicenseWithKey>("/api/v1/licenses", input),
  update: (id: number, input: LicenseInput) => http.put<License>(`/api/v1/licenses/${id}`, input),
  remove: (id: number) => http.del<void>(`/api/v1/licenses/${id}`),
  revoke: (id: number, reason: string) => http.post<License>(`/api/v1/licenses/${id}/revoke`, { reason }),
  unrevoke: (id: number) => http.post<License>(`/api/v1/licenses/${id}/unrevoke`),
  rotate: (id: number) => http.post<LicenseWithKey>(`/api/v1/licenses/${id}/rotate`),
  instances: async (id: number) => (await http.get<LicenseInstance[] | null>(`/api/v1/licenses/${id}/instances`)) ?? [],
  forgetInstance: (id: number, instanceRowId: number) => http.del<void>(`/api/v1/licenses/${id}/instances/${instanceRowId}`),
  /** Dry run: what a client presenting this key would get; registers nothing. */
  check: (key: string, productId: string) => http.post<LicenseCheckResult>("/api/v1/licenses/check", { key, product_id: productId }),
};

// ---- metrics -------------------------------------------------------------

export const securitySettingsApi = {
  get: () => http.get<SecuritySettings>("/api/v1/settings/security"),
  set: (v: SecuritySettings) => http.put<SecuritySettings>("/api/v1/settings/security", v),
};

export const metricsApi = {
  /** Prometheus text exposition (top-level /metrics, perm metrics.read). */
  scrape: () => http.get<string>("/metrics", { text: true }),
};
